import React from "react";
import {
  Leaf, AlertTriangle, CheckCircle2, Shield, Beaker, Sprout,
  Calendar, Layers, Ruler, Droplets, Wheat, BarChart3, Flower2,
} from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const severityColor = (sev) => {
  switch (sev) {
    case "high":
      return "bg-[#D90429] text-white";
    case "moderate":
      return "bg-[#E9C46A] text-[#1C1C1C]";
    case "low":
      return "bg-[#2B9348] text-white";
    default:
      return "bg-[#52796F] text-white";
  }
};

const STAGE_LABEL = {
  seedling: { en: "Seedling", hi: "अंकुरण" },
  vegetative: { en: "Vegetative", hi: "वानस्पतिक" },
  flowering: { en: "Flowering", hi: "फूल" },
  fruiting: { en: "Fruiting", hi: "फलधारण" },
  harvest: { en: "Harvest", hi: "कटाई" },
};

export const DiagnosisResult = ({ scan }) => {
  const { t, lang } = useLang();
  const r = scan.result;

  if (!r.is_plant) {
    return (
      <div
        data-testid="diagnosis-not-plant"
        className="bg-white border-l-4 border-[#E07A5F] rounded-2xl p-6 shadow-sm"
      >
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-5 h-5 text-[#E07A5F]" />
          <h3 className="cd-heading font-bold text-lg">{t("diag.notPlant")}</h3>
        </div>
        <p className="text-[#2D3A35]">{r.summary}</p>
      </div>
    );
  }

  const intent = r.intent || "disease_check";
  const intentBadge = {
    disease_check: { label: t("diag.symptoms"), color: "bg-[#E07A5F]" },
    pre_plant: { label: t("diag.prePlant.title"), color: "bg-[#D4A373]" },
    growth_check: { label: t("diag.growth.title"), color: "bg-[#2B9348]" },
  }[intent] || { label: "—", color: "bg-[#52796F]" };

  return (
    <div className="space-y-4" data-testid="diagnosis-result">
      {/* Hero result card */}
      <div
        className="bg-white border-l-4 border-[#E07A5F] rounded-2xl p-6 shadow-sm cd-fade-up"
        data-testid={`diagnosis-intent-${intent}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap text-[#52796F] text-sm font-medium">
              <Leaf className="w-4 h-4" /> {r.crop_name || "Plant"}
              <span className={`text-white text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold ${intentBadge.color}`}>
                {intentBadge.label}
              </span>
            </div>
            <h2 className="cd-heading text-2xl sm:text-3xl font-extrabold mt-1">
              {intent === "disease_check"
                ? r.healthy
                  ? t("diag.healthy")
                  : r.disease_name || t("diag.unknown")
                : intent === "pre_plant"
                ? t("diag.prePlant.title")
                : t("diag.growth.title")}
            </h2>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            {r.severity && intent === "disease_check" && !r.healthy && (
              <span
                data-testid="severity-badge"
                className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${severityColor(r.severity)}`}
              >
                {r.severity}
              </span>
            )}
            <span className="text-xs text-[#52796F]">
              {t("diag.confidence")}: <span className="font-bold text-[#1C1C1C]">{r.confidence}%</span>
            </span>
          </div>
        </div>
        {r.summary && <p className="mt-4 text-[#2D3A35] leading-relaxed">{r.summary}</p>}
      </div>

      {/* Pre-plant guide */}
      {intent === "pre_plant" && r.pre_plant_guide && (
        <div className="space-y-4" data-testid="section-pre-plant">
          <div className="grid sm:grid-cols-2 gap-4">
            {r.pre_plant_guide.season && (
              <InfoBox icon={<Calendar className="w-5 h-5 text-[#E07A5F]" />} title={t("diag.prePlant.season")} testid="pp-season">
                {r.pre_plant_guide.season}
              </InfoBox>
            )}
            {r.pre_plant_guide.spacing && (
              <InfoBox icon={<Ruler className="w-5 h-5 text-[#52796F]" />} title={t("diag.prePlant.spacing")} testid="pp-spacing">
                {r.pre_plant_guide.spacing}
              </InfoBox>
            )}
            {r.pre_plant_guide.expected_yield && (
              <InfoBox icon={<BarChart3 className="w-5 h-5 text-[#2B9348]" />} title={t("diag.prePlant.yield")} testid="pp-yield">
                {r.pre_plant_guide.expected_yield}
              </InfoBox>
            )}
          </div>
          {r.pre_plant_guide.soil?.length > 0 && (
            <Section icon={<Layers className="w-5 h-5 text-[#D4A373]" />} title={t("diag.prePlant.soil")} testid="pp-soil">
              <List items={r.pre_plant_guide.soil} />
            </Section>
          )}
          {r.pre_plant_guide.seed_selection?.length > 0 && (
            <Section icon={<Wheat className="w-5 h-5 text-[#1B4332]" />} title={t("diag.prePlant.seed")} testid="pp-seed">
              <List items={r.pre_plant_guide.seed_selection} />
            </Section>
          )}
          {r.pre_plant_guide.fertilizer?.length > 0 && (
            <Section icon={<Droplets className="w-5 h-5 text-[#52796F]" />} title={t("diag.prePlant.fertilizer")} testid="pp-fert">
              <ListWithMarket items={r.pre_plant_guide.fertilizer} kind="fertilizer" cropName={r.crop_name} />
            </Section>
          )}
          {r.pre_plant_guide.timeline?.length > 0 && (
            <Section icon={<Calendar className="w-5 h-5 text-[#1B4332]" />} title={t("diag.prePlant.timeline")} testid="pp-timeline">
              <ol className="space-y-2 list-decimal list-inside text-[#2D3A35]">
                {r.pre_plant_guide.timeline.map((it, i) => (
                  <li key={i}>{it}</li>
                ))}
              </ol>
            </Section>
          )}
        </div>
      )}

      {/* Growth check */}
      {intent === "growth_check" && (
        <div className="space-y-4" data-testid="section-growth">
          {r.growth_stage && (
            <InfoBox
              icon={<Flower2 className="w-5 h-5 text-[#2B9348]" />}
              title={t("diag.growth.stage")}
              testid="growth-stage"
            >
              <span className="font-bold capitalize">
                {STAGE_LABEL[r.growth_stage]?.[lang] || STAGE_LABEL[r.growth_stage]?.en || r.growth_stage}
              </span>
            </InfoBox>
          )}
          {r.growth_advice?.length > 0 && (
            <Section icon={<Sprout className="w-5 h-5 text-[#1B4332]" />} title={t("diag.growth.advice")} testid="growth-advice">
              <List items={r.growth_advice} />
            </Section>
          )}
        </div>
      )}

      {/* Disease check (default) */}
      {intent === "disease_check" && (
        r.healthy ? (
          <Section
            icon={<CheckCircle2 className="w-5 h-5 text-[#2B9348]" />}
            title={t("diag.greatNews")}
            testid="section-healthy"
          >
            <p className="text-[#2D3A35]">{t("diag.greatNewsBody")}</p>
          </Section>
        ) : (
          <>
            {r.symptoms?.length > 0 && (
              <Section icon={<AlertTriangle className="w-5 h-5 text-[#E07A5F]" />} title={t("diag.symptoms")} testid="section-symptoms">
                <List items={r.symptoms} />
              </Section>
            )}
            {r.causes?.length > 0 && (
              <Section icon={<Shield className="w-5 h-5 text-[#52796F]" />} title={t("diag.causes")} testid="section-causes">
                <List items={r.causes} />
              </Section>
            )}
            {(r.treatment?.organic?.length > 0 || r.treatment?.chemical?.length > 0) && (
              <div className="grid md:grid-cols-2 gap-4" data-testid="section-treatment">
                {r.treatment?.organic?.length > 0 && (
                  <Section icon={<Sprout className="w-5 h-5 text-[#2B9348]" />} title={t("diag.organic")} testid="treatment-organic">
                    <ListWithMarket items={r.treatment.organic} kind="organic" cropName={r.crop_name} />
                  </Section>
                )}
                {r.treatment?.chemical?.length > 0 && (
                  <Section icon={<Beaker className="w-5 h-5 text-[#E07A5F]" />} title={t("diag.chemical")} testid="treatment-chemical">
                    <ListWithMarket items={r.treatment.chemical} kind="chemical" cropName={r.crop_name} />
                  </Section>
                )}
              </div>
            )}
            {r.prevention?.length > 0 && (
              <Section icon={<Shield className="w-5 h-5 text-[#1B4332]" />} title={t("diag.prevention")} testid="section-prevention">
                <List items={r.prevention} />
              </Section>
            )}
          </>
        )
      )}
    </div>
  );
};

const Section = ({ icon, title, children, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl p-5 sm:p-6 border border-[#E5E0D8] shadow-sm">
    <div className="flex items-center gap-2 mb-3">
      {icon}
      <h3 className="cd-heading text-lg font-bold text-[#1C1C1C]">{title}</h3>
    </div>
    {children}
  </div>
);

const InfoBox = ({ icon, title, children, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl p-4 border border-[#E5E0D8] shadow-sm flex items-start gap-3">
    {icon}
    <div className="flex-1 min-w-0">
      <div className="text-xs font-semibold text-[#52796F] uppercase tracking-wide">{title}</div>
      <div className="text-[#1C1C1C] mt-0.5">{children}</div>
    </div>
  </div>
);

const List = ({ items }) => (
  <ul className="space-y-2">
    {items.map((it, i) => (
      <li key={i} className="flex gap-2 text-[#2D3A35]">
        <span className="text-[#D4A373] font-bold">•</span>
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const ListWithMarket = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((it, i) => (
      <li key={i} className="flex gap-2 text-[#2D3A35]">
        <span className="text-[#D4A373] font-bold">•</span>
        <span className="flex-1">{it}</span>
      </li>
    ))}
  </ul>
);
